import { PostHogProvider } from "@posthog/react";
import type { CaptureResult } from "posthog-js";
import { createRoot } from "react-dom/client";
import { sanitizeProperties } from "./analytics/sanitizeUrl";
import App from "./App";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import ErrorBoundary from "./theme/ErrorBoundary";
import { bootstrapNativeWebViewInsets } from "./utils/nativeWebViewInsets";
import { bootstrapNativeAuthBridge } from "./utils/nativeAuthBridge";
import { bootstrapNativeCheckoutBridge } from "./utils/nativeCheckoutBridge";

// Load timings: dom_ready set in index.html (before any bundle); react_ready set below; auth_bootstrap in AuthContext
if (typeof window !== "undefined") {
  window.__loadTimings = window.__loadTimings ?? {};
  bootstrapNativeWebViewInsets();
  bootstrapNativeAuthBridge();
  bootstrapNativeCheckoutBridge();
}

const posthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
const posthogEnabled =
  process.env.NODE_ENV !== "development" &&
  typeof posthogKey === "string" &&
  posthogKey.length > 0;

// Expose for debugging (e.g. in console: window.__posthogEnabled). console.log is often stripped in prod builds.
if (typeof window !== "undefined") {
  window.__posthogEnabled = posthogEnabled;
}
console.warn("posthogEnabled", posthogEnabled);

// Magic link, invite and password-reset URLs carry a credential in the path or
// query, and PostHog records the URL on pageviews, autocapture and $referrer.
// Scrub every outgoing event rather than trusting individual call sites.
const scrubCredentials = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  return {
    ...event,
    properties: sanitizeProperties(event.properties ?? {}),
    ...(event.$set ? { $set: sanitizeProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: sanitizeProperties(event.$set_once) } : {})
  };
};

const container = document.getElementById("root")!;
const root = createRoot(container);
root.render(
  // <React.StrictMode>
  <ErrorBoundary showDebugInfo={process.env.NODE_ENV === "development"}>
    {posthogEnabled ? (
      <PostHogProvider
        apiKey={posthogKey}
        options={{
          api_host: posthogHost,
          defaults: "2026-01-30",
          capture_pageview: false,
          autocapture: {
            css_selector_allowlist: ['[data-attr]', '[data-testid]', '[data-custom-id]'],
          },
          before_send: scrubCredentials,
        }}
      >
        <App />
      </PostHogProvider>
    ) : (
      <App />
    )}
  </ErrorBoundary>
  // </React.StrictMode>
);

if (typeof window !== "undefined" && window.__loadTimings) {
  requestAnimationFrame(() => {
    if (window.__loadTimings && window.__loadTimings.react_ready_ms == null) {
      window.__loadTimings.react_ready_ms = performance.now();
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
